import styled from 'styled-components';
import {
  breakpoint,
  Modal as ModalComponent,
  ModalProps as ComponentProps,
  CheckIcon,
  PrimaryButton,
} from '@grays-npm/component-library';
import ReactDOM from 'react-dom';
import { useLayoutEffect, useState } from 'react';

type ModalSize = 'lg' | 'auto';

export type ModalProps = ComponentProps & {
  isOpen?: boolean;
  isButtonLoading?: boolean;
  isSuccess?: boolean;
  successText?: { title: string; body: string };
  modalSize?: ModalSize;
  children?: React.ReactNode;
};

const animationDuration = 200;

type OverlayProps = {
  isVisible: boolean;
};

const Overlay = styled.div<OverlayProps>`
  align-items: flex-end;
  background-color: ${({ theme }) => theme.colors.neutrals.transparentGrays[4]};
  display: flex;
  justify-content: center;
  opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  transition: opacity ${animationDuration}ms, visibility ${animationDuration}ms;
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  width: 100vw;
  height: 100vh;
  ${breakpoint('sm')`
    align-items: center;
  `}
`;

const StyledModalComponent = styled(ModalComponent)<
  OverlayProps & { modalSize?: ModalSize }
>`
  transform: ${({ isVisible }) => (isVisible ? 'scaleY(1)' : 'scaleY(0)')};
  transform-origin: bottom;
  transition: transform ${animationDuration}ms ease;

  ${({ modalSize }) => modalSize === 'lg' && 'width: 780px'};

  ${breakpoint('sm')`
    transition-duration: 0s;
  `}
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const CheckCircleBack = styled(CheckIcon)`
  background: ${({ theme }) => theme.colors.system.systemGreen};
  border-radius: 50%;
  fill: ${({ theme }) => theme.colors.primary.white};
`;

const ContinueButton = styled(PrimaryButton)`
  margin-top: ${({ theme }) => theme.spacings[5].rem};
  min-width: 184px;
`;

export const Modal: React.FC<ModalProps> = ({
  children,
  isOpen = false,
  onClose,
  title,
  titleVariant = 'primary',
  isSuccess,
  successText,
  modalSize = 'auto',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const overlayId = 'modal-overlay';
  useLayoutEffect(() => {
    setIsVisible(isOpen);
    const overflowStyle = isOpen ? 'hidden' : 'initial';
    document.body.style.overflow = overflowStyle;
    const root = document.getElementById('root');
    if (root) root.style.overflow = overflowStyle;
  }, [isOpen]);

  const handleClick = (event?: React.MouseEvent, forceClose = false) => {
    if (forceClose || (event?.target as Element)?.id === overlayId) {
      setIsVisible(false);
      setTimeout(onClose, animationDuration);
    }
  };

  const handleClose = () => {
    handleClick(undefined, true);
  };

  return ReactDOM.createPortal(
    <Overlay id={overlayId} isVisible={isVisible} onClick={handleClick}>
      <StyledModalComponent
        onClose={handleClose}
        title={isSuccess ? undefined : title}
        titleVariant={titleVariant}
        isVisible={isVisible}
        modalSize={modalSize}
      >
        {isSuccess ? (
          <SuccessContainer>
            <CheckCircleBack width={64} />
            <h3>{successText?.title}</h3>
            <div>{successText?.body}</div>
            <ContinueButton onClick={onClose}>Continue</ContinueButton>
          </SuccessContainer>
        ) : (
          children
        )}
      </StyledModalComponent>
    </Overlay>,
    document.getElementById('modal-root') as HTMLElement
  );
};
